import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MultiStepFormLayout from '../Layout/FormLayout';
import { InputBase } from '@mantine/core';
import {
  decrementStep,
  incrementStep,
  setCurrentStep,
  updateFormData,
} from '../features/FormRegistration';
import { IMaskInput } from 'react-imask';

import { CoverIsFor } from '../types/types';
import PrimaryButton from '../components/PrimaryButton';
import { calculateLHC, pushDataLayer } from '../utils/helpers';

import SecondaryButton from '../components/SecondaryButton';
import LeftChevron from '../icons/LeftChevron';
import { isOver18, isValidDate } from '../utils/form/helpers';
import { useForm } from '@mantine/form';

function Step2() {
  const { formData } = useSelector((state: any) => state.formRegistration);

  const form = useForm({
    mode: 'controlled',
    initialValues: {
      customerBirthDate: formData.dateOfBirth[0] || '',
      partnerBirthDate: formData.dateOfBirth[1] || '',
    },

    validate: {
      customerBirthDate: (value) => {
        if (!isValidDate(value)) {
          return 'Invalid date';
        }
        if (!isOver18(value)) {
          return 'User must be at least 18 years old';
        }
        return null;
      },
      partnerBirthDate: (value) => {
        if (
          formData.coverIsFor === CoverIsFor.Couple ||
          formData.coverIsFor === CoverIsFor.Family
        ) {
          if (!isValidDate(value)) {
            return 'Invalid date';
          }
          if (!isOver18(value)) {
            return 'Partner must be at least 18 years old';
          }
        }
        return null;
      },
    },
  });

  const dispatch = useDispatch();
  const userDate = formData.dateOfBirth[0];
  const partnerDate = formData.dateOfBirth[1];

  const { isMobileView } = useSelector((state: any) => state.viewport);
  const isButtonDisabled = () => {
    const isUserDateValid = isValidDate(userDate);
    const isPartnerDateValid =
      formData.coverIsFor === CoverIsFor.Couple ||
      formData.coverIsFor === CoverIsFor.Family
        ? isValidDate(partnerDate)
        : true;

    const isUserOver18 = isUserDateValid && isOver18(userDate);
    const isPartnerOver18 =
      isPartnerDateValid &&
      (formData.coverIsFor === CoverIsFor.Couple ||
      formData.coverIsFor === CoverIsFor.Family
        ? isOver18(partnerDate)
        : true);

    return (
      !isUserDateValid ||
      !isUserOver18 ||
      !isPartnerDateValid ||
      !isPartnerOver18
    );
  };

  const handleFormSubmit = () => {
    pushDataLayer('funnel_birthday', {
      birthday: userDate,
      ...(partnerDate ? { partner_birthday: partnerDate } : {}),
    });

    const { customerBirthDate, partnerBirthDate } = form.values;
    let lhcLoadingCustomer = null;
    let lhcLoadingPartner = null;

    if (customerBirthDate) {
      lhcLoadingCustomer = calculateLHC(customerBirthDate);
    }

    if (partnerBirthDate) {
      lhcLoadingPartner = calculateLHC(partnerBirthDate);
    }

    dispatch(
      updateFormData({
        ...formData,
        dateOfBirth: [
          form.values.customerBirthDate,
          form.values.partnerBirthDate,
        ],
        customerLoading: lhcLoadingCustomer,
        partnerLoading: lhcLoadingPartner,
      }),
    );
    if (
      formData.coverIsFor === CoverIsFor.Family ||
      formData.coverIsFor === CoverIsFor.SingleParentFamily
    ) {
      dispatch(incrementStep());
    } else {
      dispatch(setCurrentStep(4));
    }
  };

  if (isMobileView) {
    return (
      <MultiStepFormLayout>
        <form
          onSubmit={form.onSubmit(handleFormSubmit)}
          className="flex flex-col items-center h-full justify-between w-full">
          <h1 className="text-[22px] text-start font-bold self-start">
            What is your date of birth?
          </h1>

          <InputBase
            className="w-full text-start"
            label="Date of Birth"
            withAsterisk
            classNames={{
              input: '!bg-[white] !h-[48px]',
            }}
            {...form.getInputProps('customerBirthDate')}
            placeholder="dd/mm/yyyy"
            component={IMaskInput}
            mask="00/00/0000"
            onAccept={(val) => {
              dispatch(
                updateFormData({
                  ...formData,
                  dateOfBirth: [val, ...formData.dateOfBirth.slice(1)],
                }),
              );
              form.setFieldValue('customerBirthDate', val);
              //update form
            }}
          />

          {(formData.coverIsFor === CoverIsFor.Couple ||
            formData.coverIsFor === CoverIsFor.Family) && (
            <div className="my-[20px] w-full">
              <h1 className="text-[22px] font-bold text-start self-start">
                What is your partner's date of birth?
              </h1>
              <InputBase
                className="w-full text-start"
                label="Partner's Date of Birth"
                withAsterisk
                classNames={{
                  input: '!bg-[white] !h-[48px]',
                }}
                {...form.getInputProps('partnerBirthDate')}
                placeholder="dd/mm/yyyy"
                component={IMaskInput}
                mask="00/00/0000"
                value={form.values.partnerBirthDate}
                onAccept={(val) => {
                  dispatch(
                    updateFormData({
                      ...formData,
                      dateOfBirth: [formData.dateOfBirth[0], val],
                    }),
                  );
                  //update form
                  form.setFieldValue('partnerBirthDate', val);
                }}
              />
            </div>
          )}

          <div className="flex flex-col items-start text-start w-full">
            <span className="font-bold text-[14px]">
              Why We Need Your Date of Birth
            </span>
            <p className="font-normal text-[14px]">
              Providing your date of birth enables us to offer you more accurate
              pricing for your health insurance. Age plays a crucial role in
              determining various factors, including:
              <br />- The Australian Government Rebate on health insurance.
              <br />- Lifetime Health Cover (LHC) loading.
              <br />- Age-based discounts.
            </p>
          </div>
          <div className="w-full justify-between h-full flex  items-center gap-[10px] sticky bottom-0 bg-[white] p-4 z-10 border-t border-[#D1D7DF] mt-6">
            <SecondaryButton
              leftSection={<LeftChevron />}
              onClick={() => {
                dispatch(decrementStep());
              }}>
              Previous
            </SecondaryButton>
            <PrimaryButton
              style={{
                marginTop: '20px',
                marginBottom: '20px',
                alignSelf: 'center',
              }}
              classNames="w-[50%] md:w-auto"
              disabled={isButtonDisabled()}
              text="Continue"
              type="submit"
            />{' '}
          </div>
        </form>
      </MultiStepFormLayout>
    );
  }

  return (
    <MultiStepFormLayout>
      <form
        className="flex flex-col items-center h-full justify-between w-full"
        onSubmit={form.onSubmit(handleFormSubmit)}>
        <h1 className="text-[22px] text-start font-bold self-start">
          What is your date of birth?
        </h1>

        <InputBase
          className="w-full text-start"
          label="Date of Birth"
          withAsterisk
          classNames={{
            input: '!bg-[white] !h-[48px]',
          }}
          placeholder="dd/mm/yyyy"
          component={IMaskInput}
          mask="00/00/0000"
          {...form.getInputProps('customerBirthDate')}
          value={form.values.customerBirthDate}
          onAccept={(val) => {
            dispatch(
              updateFormData({
                ...formData,
                dateOfBirth: [val, ...formData.dateOfBirth.slice(1)],
              }),
            );
            form.setFieldValue('customerBirthDate', val);
          }}
        />

        {(formData.coverIsFor === CoverIsFor.Couple ||
          formData.coverIsFor === CoverIsFor.Family) && (
          <div className="my-[20px] w-full">
            <h1 className="text-[22px] font-bold text-start self-start">
              What is your partner's date of birth?
            </h1>
            <InputBase
              className="w-full text-start"
              label="Partner's Date of Birth"
              withAsterisk
              classNames={{
                input: '!bg-[white] !h-[48px]',
              }}
              placeholder="dd/mm/yyyy"
              component={IMaskInput}
              mask="00/00/0000"
              {...form.getInputProps('partnerBirthDate')}
              value={form.values.partnerBirthDate}
              onAccept={(val) => {
                dispatch(
                  updateFormData({
                    ...formData,
                    dateOfBirth: [formData.dateOfBirth[0], val],
                  }),
                );
                form.setFieldValue('partnerBirthDate', val);
              }}
            />
          </div>
        )}

        <div className="flex flex-col items-start text-start w-full">
          <span className="font-bold text-[14px] mt-4">
            Why We Need Your Date of Birth
          </span>
          <p className="font-normal text-[14px]">
            Providing your date of birth enables us to offer you more accurate
            pricing for your health insurance. Age plays a crucial role in
            determining various factors, including:
            <br />- The Australian Government Rebate on health insurance.
            <br />- Lifetime Health Cover (LHC) loading.
            <br />- Age-based discounts.
          </p>
        </div>
        <div className="w-full justify-between h-full flex  items-center gap-[10px] sticky bottom-0 bg-[white] p-4 z-10 border-t border-[#D1D7DF] mt-6">
          <SecondaryButton
            leftSection={<LeftChevron />}
            onClick={() => {
              dispatch(decrementStep());
            }}>
            Previous
          </SecondaryButton>
          <PrimaryButton
            style={{
              marginTop: '20px',
              marginBottom: '20px',
              alignSelf: 'center',
            }}
            classNames="w-[50%] md:w-auto"
            disabled={isButtonDisabled()}
            text="Continue"
            type="submit"
          />{' '}
        </div>
      </form>
    </MultiStepFormLayout>
  );
}

export default Step2;
